import { createSlice } from '@reduxjs/toolkit';
import { CriticalWeightInput, DocProcessingRate, initialState, MeteredRate, PostageRateV2 } from './constants';
import {
  changeJobStatus,
  deactivateMember,
  demoteMember,
  editCustomer,
  editSheetPrices,
  getAdminMembers,
  getCustomers,
  getDocSheets,
  getDocumentProcessingRates,
  getMeteredRates,
  getPostageRates,
  getPostageRatesv2,
  getPriceSheets,
  getPromoCodes,
  getSheetPriceList,
  getWeightCriticalInput,
  promoteMember,
  updateJobTrackingNumbers,
} from './utils';

const groupByMany = (
  items: any[],
  keys: string[],
  transformers: Record<string, (value: any) => any> = {}
) => {
  const getValue = (item: any, key: string) => {
    const value = item[key];
    return transformers[key] ? transformers[key](value) : value;
  };

  return items.reduce((acc, item) => {
    let currentLevel = acc;

    keys.forEach((key, index) => {
      const value = getValue(item, key);

      const isLast = index === keys.length - 1;

      if (isLast) {
        (currentLevel[value] ??= []).push(item);
      } else {
        currentLevel[value] ??= {};
        currentLevel = currentLevel[value];
      }
    });

    return acc;
  }, {} as Record<string, any>);
};

const mailingTypeGroup = (value: string) => {
  const idx = value.lastIndexOf("_");
  return idx === -1 ? value : value.substring(0, idx);
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    resetAdminState: () => initialState,
    updateCustomers: (state, action) => {
      state.customers = action.payload;
    },
    updatePromoCodeForm: (state, action) => {
      state.promoCodeForm = action.payload;
    },
    updatePromoCodes: (state, action) => {
      state.promoCodes = action.payload;
    },
    setIsAddPromoCodeModalOpen: (state, action) => {
      state.isAddPromoCodeModalOpen = action.payload;
    },
    updateUnsavedPostageRates: (state, action) => {
      state.unsavedPostageRates = { ...state.unsavedPostageRates, [action.payload.id]: action.payload.value };
      // change in domestic flat rate envelope/legal flat rate envelope should reflect all domestic flat rate envelopes
      // same for canada, international, mexico
      const itemsToUpdate = state.postageRatesResponse.filter(
        (item: PostageRateV2) =>
          item.packageType !== '6x9' &&
          item.packageType !== 'flat' &&
          item.packageType !== '#10' &&
          item.id !== action.payload.id &&
          item.packageType === action.payload.packageType &&
          item.mailingType === action.payload.mailingType
      );
      const newItems = itemsToUpdate.map((item) => ({
        id: item.id,
        price: +action.payload.value.slice(1),
      }));
      state.updatedEnvelopeRates = newItems.reduce((acc, { id, price }) => {
        acc[id] = price;
        return acc;
      }, state.updatedEnvelopeRates);
    },
    clearUnsavedPostageRates: (state) => {
      state.unsavedPostageRates = {};
      state.updatedEnvelopeRates = {};
    },
    updateUnsavedMeteredInputs: (state, action) => {
      state.unsavedMeteredInputs[action.payload.name] = action.payload.value;
    },
    clearUnsavedMeteredInputs: (state) => {
      state.unsavedMeteredInputs = {};
    },
    updateUnsavedWeightChanges: (state, action) => {
      state.unsavedWeightChanges[action.payload.name] = action.payload.value;
    },
    clearUnsavedWeightChanges: (state) => {
      state.unsavedWeightChanges = {};
    },
    updateUnsavedDocProcessingRates: (state, action) => {
      state.unsavedDocProcessingRatesChanges[action.payload.id] = action.payload.value;
    },
    clearUnsavedDocProcessingRates: (state) => {
      state.unsavedDocProcessingRatesChanges = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAdminMembers.fulfilled, (state, action) => {
        state.members = action.payload.items;
      })
      .addCase(getAdminMembers.rejected, (state, action) => {
        state.fetchError = action.payload ? (action.payload as string) : 'Something went wrong!';
      })
      // .addCase(getCompanyInfo.fulfilled, (state, action) => {
      //   state.info = action.payload.data;
      // })
      // .addCase(getCompanyInfo.rejected, (state, action) => {
      //   state.fetchError = action.payload ? (action.payload as string) : 'Something went wrong!';
      //   state.loadingTable = false;
      // })
      // .addCase(inviteMember.fulfilled, (state) => {
      //   state.newUserForm.errorMsg = '';
      //   state.newUserForm.successMsg = `Invitation sent to: ${state.newUserForm.value}`;
      // })
      // .addCase(inviteMember.rejected, (state, action) => {
      //   state.newUserForm.successMsg = '';
      //   console.log(action.payload);
      //   state.newUserForm.errorMsg =
      //     action.payload && action.payload === 'Validation error at body'
      //       ? 'Wront email format!'
      //       : 'Something went wrong!';
      // })
      // .addCase(promoteMember.fulfilled, (state, action) => {
      //   state.members = state.members.map((item) =>
      //     item.id === action.payload.memberId ? { ...item, role: action.payload.role } : item,
      //   );
      // })
      // .addCase(demoteMember.fulfilled, (state, action) => {
      //   state.members = state.members.map((item) =>
      //     item.id === action.payload.memberId ? { ...item, role: action.payload.role } : item,
      //   );
      // })
      // .addCase(acceptInvitation.pending, (state) => {
      //   state.isProcessingInvitation = true;
      // })
      // .addCase(acceptInvitation.fulfilled, (state, action) => {
      //   state.isProcessingInvitation = false;
      //   state.members = state.members.map((item) =>
      //     item.id === Number(action.payload) ? { ...item, status: 'joined' } : item,
      //   );
      // })
      // .addCase(acceptInvitation.rejected, (state) => {
      //   state.isProcessingInvitation = false;
      // })
      // .addCase(denyInvitation.pending, (state) => {
      //   state.isProcessingInvitation = true;
      // })
      // .addCase(denyInvitation.fulfilled, (state, action) => {
      //   state.isProcessingInvitation = false;
      //   state.members = state.members.filter((item) => item.id !== Number(action.payload));
      // })
      // .addCase(denyInvitation.rejected, (state) => {
      //   state.isProcessingInvitation = false;
      // });
      .addCase(getCustomers.pending, (state) => {
        state.isFetchingOrganizations = true;
        state.fetchOrganizationsError = '';
      })
      .addCase(getCustomers.fulfilled, (state, action) => {
        state.isFetchingOrganizations = false;
        state.customers = action.payload.items;
        state.fetchOrganizationsError = '';
      })
      .addCase(getCustomers.rejected, (state, action) => {
        state.isFetchingOrganizations = false;
        state.fetchOrganizationsError = 'Something went wrong';
      })
      .addCase(editCustomer.rejected, (state, action) => {
        const { message, errorStack } = action.payload as { message: string; errorStack: string[] };

        const errors: Record<string, boolean> = {};

        if (message === "Organization name can't be used") {
          errors['nameExists'] = true;
        }

        errorStack.forEach((message) => {
          if (message.includes("Organization name can't be used")) {
            errors['nameExists'] = true;
          }
          if (message.includes("Domain name can't be used")) {
            errors['domainError'] = true;
          }
        });

        state.editOrganizationError = errors;
      })
      .addCase(getPriceSheets.fulfilled, (state, action) => {
        state.priceSheets = action.payload.items;
      })
      .addCase(getPostageRates.fulfilled, (state, action) => {
        const groupedData = action.payload.items.reduce((acc: any, item: any) => {
          if (!acc[item.postageType]) acc[item.postageType] = {};
          if (!acc[item.postageType][item.tariffType]) acc[item.postageType][item.tariffType] = [];

          acc[item.postageType][item.tariffType].push({ ...item, price: `$${item.price}` });

          return acc;
        }, {});

        Object.values(groupedData).forEach((tarrifGroups) => {
          Object.values(tarrifGroups as any).forEach((items) => {
            (items as any).sort((a: any, b: any) => a.weight - b.weight);
          });
        });
        state.postageRates = action.payload.items;
        state.transformedPostageRates = groupedData;
      })
      .addCase(getSheetPriceList.fulfilled, (state, action) => {
        state.sheetPricePayload = { printPriceAdjustments: [], printPriceCosts: [] };
        state.isFetchingSheetPrices = false;

        const [costData, adjustmentData] = action.payload;
        const groupBy = (items: any, key: any) =>
          items.reduce((acc: any, item: any) => ((acc[item[key]] ??= []).push(item), acc), {} as Record<string, any[]>);
        const groupedCost = groupBy(costData.items, 'unitType');
        const desiredOrder = [
          'first_class',
          'certified',
          'mexico',
          'canada',
          'international',
          'first_class_overnight',
          'international_overnight',
        ];

        Object.keys(groupedCost).forEach((unitType) => {
          groupedCost[unitType] = groupBy(groupedCost[unitType], 'postageType');
          groupedCost[unitType] = desiredOrder.reduce((sorted: Record<string, any[]>, type) => {
            if (groupedCost[unitType][type]) sorted[type] = groupedCost[unitType][type];
            return sorted;
          }, {});
        });

        const groupedAdjustment = groupBy(adjustmentData.items, 'category');
        groupedAdjustment.volume_discounts.sort((a: any, b: any) => a.name.localeCompare(b.name));

        const mergeCosts = (type: string) => [
          ...(groupedCost.envelope?.[type] || []),
          ...(groupedCost.page?.[type] || []),
        ];

        state.transformedSheetPriceData = {
          ...Object.fromEntries(desiredOrder.map((type) => [type, mergeCosts(type)])),
          ...groupedAdjustment,
        };
      })
      .addCase(getSheetPriceList.pending, (state) => {
        state.isFetchingSheetPrices = true;
      })
      .addCase(getSheetPriceList.rejected, (state) => {
        state.isFetchingSheetPrices = false;
      })
      .addCase(editSheetPrices.fulfilled, (state) => {
        state.sheetPricePayload = { printPriceAdjustments: [], printPriceCosts: [] };
      })
      .addCase(editSheetPrices.rejected, (state) => {
        state.sheetPricePayload = { printPriceAdjustments: [], printPriceCosts: [] };
      })
      .addCase(getPromoCodes.fulfilled, (state, action) => {
        state.promoCodes = action.payload.items;
        state.isLoadingPromoCodes = false;
      })
      .addCase(getPromoCodes.pending, (state) => {
        state.isLoadingPromoCodes = true;
      })
      .addCase(getPromoCodes.rejected, (state) => {
        state.isLoadingPromoCodes = false;
      })
      .addCase(promoteMember.fulfilled, (state, action) => {
        const updatedMembers = state.members.map((member) =>
          member.id === action.payload ? { ...member, role: 'admin' } : member,
        );
        state.members = updatedMembers;
      })
      .addCase(demoteMember.fulfilled, (state, action) => {
        const updatedMembers = state.members.map((member) =>
          member.id === action.payload ? { ...member, role: 'support' } : member,
        );
        state.members = updatedMembers;
      })
      .addCase(deactivateMember.fulfilled, (state, action) => {
        const updatedMembers = state.members.filter((member) => member.id !== action.payload);
        state.members = updatedMembers;
      })
      .addCase(changeJobStatus.pending, (state) => {
        state.isChangingJobStatus = true;
      })
      .addCase(changeJobStatus.fulfilled, (state) => {
        state.isChangingJobStatus = false;
      })
      .addCase(changeJobStatus.rejected, (state) => {
        state.isChangingJobStatus = false;
      })
      .addCase(updateJobTrackingNumbers.pending, (state) => {
        state.isUpdatingJobTrackingNumbers = true;
      })
      .addCase(updateJobTrackingNumbers.fulfilled, (state) => {
        state.isUpdatingJobTrackingNumbers = false;
      })
      .addCase(updateJobTrackingNumbers.rejected, (state) => {
        state.isUpdatingJobTrackingNumbers = false;
      })
      .addCase(getPostageRatesv2.fulfilled, (state, action) => {
        const groupedRatesByMailingType = groupByMany(action.payload.items, ['mailingType', 'weightNotOverOz'], {
          mailingType: mailingTypeGroup,
        });
        state.sortedPostageRatesV2 = groupedRatesByMailingType;
        state.postageRatesResponse = action.payload.items;
      })
      .addCase(getMeteredRates.fulfilled, (state, action) => {
        const meteredRates: Record<'cert_err' | 'paf_discount', number> = {
          cert_err: 0,
          paf_discount: 0,
        };
        action.payload.items.forEach(
          (item: MeteredRate) => meteredRates[item.name] = item.price
        );
        state.meteredRates = meteredRates;
      })
      .addCase(getWeightCriticalInput.fulfilled, (state, action) => {
        const correctOrder = [
          'sheet',
          '#10',
          '6x9',
          'flat',
          'flat_rate_envelope',
          'legal_flat_rate_envelope',
          'flat_rate_box',
          'return_envelope',
        ];
        state.weightCriticalInput = action.payload.items.sort(
          (a: CriticalWeightInput, b: CriticalWeightInput) => correctOrder.indexOf(a.name) - correctOrder.indexOf(b.name),
        );
      })
      .addCase(getDocumentProcessingRates.fulfilled, (state, action) => {
        const groupedDocProcessingRates = groupByMany(action.payload.items, ['pricingUnit']);
        const perPageSequence = ['standard_page', 'rush_service', 'color_ink'];
        const perAddressSequence = [
          'colored_paper',
          'return_envelope',
          'flat',
          'flat_rate_envelope',
          'legal_flat_rate_envelope',
          'flat_rate_box',
        ];
        const perOrderSequence = ['print_minimum', 'special_cos'];
        state.documentProcessingRates = {
          per_page: (groupedDocProcessingRates.per_page ?? []).sort(
            (a: DocProcessingRate, b: DocProcessingRate) => perPageSequence.indexOf(a.name) - perPageSequence.indexOf(b.name),
          ),
          per_address: (groupedDocProcessingRates.per_address ?? []).sort(
            (a: DocProcessingRate, b: DocProcessingRate) =>
              perAddressSequence.indexOf(a.name) - perAddressSequence.indexOf(b.name),
          ),
          per_order: (groupedDocProcessingRates.per_order ?? []).sort(
            (a: DocProcessingRate, b: DocProcessingRate) =>
              perOrderSequence.indexOf(a.name) - perOrderSequence.indexOf(b.name),
          ),
        };
      })
      .addCase(getDocSheets.fulfilled, (state, action) => {
        state.docSheets = action.payload.items;
      })
  },
});

export const {
  resetAdminState,
  updatePromoCodeForm,
  updatePromoCodes,
  setIsAddPromoCodeModalOpen,
  updateCustomers,
  updateUnsavedPostageRates,
  clearUnsavedPostageRates,
  updateUnsavedMeteredInputs,
  clearUnsavedMeteredInputs,
  updateUnsavedWeightChanges,
  clearUnsavedWeightChanges,
  updateUnsavedDocProcessingRates,
  clearUnsavedDocProcessingRates,
} = adminSlice.actions;

export default adminSlice.reducer;
